import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "@/components/site/Logo";
import { Magnetic } from "@/components/site/Magnetic";
import { NAV_LINKS } from "@/data/site";

const SECTION_IDS = ["hero", "values", "playground", "collection", "metrics"];

const smoothTo = (href) => {
  const el = document.querySelector(href);
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

export const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  const [dark, setDark] = useState(false);
  const [active, setActive] = useState("hero");
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > window.innerHeight * 0.7);
      // color echo: is a dark section under the nav band?
      const el = document.elementFromPoint(window.innerWidth / 2, 40);
      const darkSection = el?.closest('[data-theme="dark"]');
      setDark(Boolean(darkSection));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) setActive(e.target.id);
        });
      },
      { rootMargin: "-45% 0px -50% 0px" }
    );
    SECTION_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  const textColor = dark ? "#FFFFFF" : "#0A0A0A";

  return (
    <motion.header
      data-testid="site-nav"
      className="fixed inset-x-0 top-0 z-[9990] transition-colors duration-500"
      animate={{
        backgroundColor: dark
          ? "rgba(10,10,10,0.72)"
          : scrolled
          ? "rgba(243,238,228,0.8)"
          : "rgba(243,238,228,0)",
      }}
      style={{
        backdropFilter: scrolled || dark ? "blur(14px)" : "none",
        WebkitBackdropFilter: scrolled || dark ? "blur(14px)" : "none",
        borderBottom: `1px solid ${
          dark ? "rgba(255,255,255,0.14)" : scrolled ? "#E5E5E5" : "transparent"
        }`,
      }}
    >
      <nav className="mx-auto flex h-[68px] max-w-[1440px] items-center justify-between px-5 md:px-10">
        <button
          data-testid="nav-logo"
          onClick={() => smoothTo("#hero")}
          className="flex items-center"
          aria-label="OWOLOVO - do góry"
        >
          <Logo inverse={dark} />
        </button>

        <div className="hidden items-center gap-9 lg:flex">
          {NAV_LINKS.map((link) => {
            const id = link.href.slice(1);
            const isActive = active === id;
            return (
              <button
                key={link.href}
                data-testid={`nav-link-${id}`}
                onClick={() => smoothTo(link.href)}
                className={`relative text-sm transition-opacity hover:opacity-100 ${
                  link.href === "#metrics" ? "font-bold" : "font-medium"
                }`}
                style={{ color: textColor, opacity: isActive ? 1 : 0.6 }}
              >
                {link.label}
                <motion.span
                  className="absolute -bottom-1 left-0 h-[2px] bg-owo-red"
                  animate={{ width: isActive ? "100%" : "0%" }}
                  transition={{ duration: 0.3 }}
                />
              </button>
            );
          })}
        </div>

        <div className="flex items-center gap-3">
          <button
            data-testid="nav-mobile-toggle"
            className="lg:hidden"
            onClick={() => setOpen((v) => !v)}
            aria-label="Menu"
            style={{ color: textColor }}
          >
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            className="overflow-hidden border-t border-hair bg-paper lg:hidden"
          >
            <div className="flex flex-col px-5 py-4">
              {NAV_LINKS.map((link) => (
                <button
                  key={link.href}
                  data-testid={`nav-mobile-${link.href.slice(1)}`}
                  onClick={() => {
                    smoothTo(link.href);
                    setOpen(false);
                  }}
                  className="border-b border-hair py-3 text-left font-heading text-2xl font-bold text-ink"
                >
                  {link.label}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};
